// ---------------------------------------------------------------------------
// Tenant Admin module — Cases list page
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { admin } from '@noxverify/api-contracts';
import { api } from '../lib/api';
import { useProjectContext } from '../lib/project-context';
import { DataTable, type Column } from '../components/DataTable';
import { StatusBadge } from '../components/StatusBadge';
import { LoadingState } from '../components/LoadingState';
import { ErrorState } from '../components/ErrorState';
import { SearchInput } from '../components/SearchInput';
import { useFormatters } from '../hooks/useFormatters';

const PAGE_SIZE = 50;

export function CasesPage() {
  const { t } = useTranslation('admin');
  const f = useFormatters();
  const caseTypeLabels = t('cases.types', { returnObjects: true }) as Record<string, string>;
  const columns = useMemo<Column<admin.CaseSummary>[]>(
    () => [
      {
        key: 'id',
        header: t('cases.columns.id'),
        render: (c) => (
          <span className="text-sm font-mono text-primary hover:text-primary underline underline-offset-2">
            {c.id}
          </span>
        ),
      },
      {
        key: 'caseType',
        header: t('cases.columns.type'),
        render: (c) => (
          <span className="text-sm text-ink">
            {caseTypeLabels[c.caseType] ?? c.caseType}
          </span>
        ),
      },
      {
        key: 'status',
        header: t('cases.columns.status'),
        render: (c) => <StatusBadge value={c.status} />,
      },
      {
        key: 'jurisdictionId',
        header: t('cases.columns.jurisdiction'),
        render: (c) => (
          <span className="text-sm text-ink-muted">{c.jurisdictionId}</span>
        ),
      },
      {
        key: 'createdAt',
        header: t('cases.columns.created'),
        render: (c) => (
          <span className="text-sm text-ink-muted tabular-nums font-mono">
            {f.date(c.createdAt, { dateStyle: 'medium' })}
          </span>
        ),
      },
      {
        key: 'decidedAt',
        header: t('cases.columns.decided'),
        render: (c) => (
          <span className="text-sm text-ink-muted">
            {c.decidedAt ? f.date(c.decidedAt, { dateStyle: 'medium' }) : '--'}
          </span>
        ),
      },
    ],
    [f, t, caseTypeLabels],
  );
  const { projectId } = useProjectContext();
  const navigate = useNavigate();
  const [cases, setCases] = useState<admin.CaseSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const [currentCursor, setCurrentCursor] = useState<string>('');

  const loadCases = useCallback((cursor?: string) => {
    if (!projectId) {
      setCases([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    api.admin
      .listCases(projectId, { limit: PAGE_SIZE, cursor })
      .then((res) => {
        setCases(res.data);
        setNextCursor(res.page?.nextCursor ?? null);
      })
      .catch((err: unknown) => {
        const message =
          err instanceof Error
            ? err.message
            : typeof err === 'object' && err !== null && 'message' in err
              ? String((err as { message: unknown }).message)
              : t('cases.errors.loadFailed');
        setError(message);
      })
      .finally(() => setLoading(false));
  }, [projectId, t]);

  useEffect(() => {
    loadCases();
  }, [loadCases]);

  // useMemo MUST be called before any conditional return (Rules of Hooks)
  const filteredCases = useMemo(() => {
    let result = cases;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (c) =>
          c.id.toLowerCase().includes(q) ||
          c.caseType.toLowerCase().includes(q),
      );
    }
    if (statusFilter) {
      result = result.filter((c) => c.status === statusFilter);
    }
    return result;
  }, [cases, searchQuery, statusFilter]);

  const statusOptions = useMemo(() => {
    const statuses = new Set(cases.map((c) => c.status));
    return Array.from(statuses).sort();
  }, [cases]);

  if (!projectId) {
    return (
      <div className="p-6">
        <h1 className="font-display text-3xl text-ink mb-4">{t('cases.headings.list')}</h1>
        <div className="bg-primary-tint border border-primary/30 rounded-sm p-4 text-primary">
          <p className="text-sm">
            {t('cases.messages.selectProject')}
          </p>
        </div>
      </div>
    );
  }

  if (loading) return <LoadingState rows={5} />;
  if (error) return <ErrorState message={error} onRetry={loadCases} />;

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-3xl text-ink">{t('cases.headings.list')}</h1>
      </div>

      <div className="mb-4 flex flex-col sm:flex-row gap-3">
        <div className="max-w-sm flex-1">
          <SearchInput
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder={t('cases.filters.searchPlaceholder')}
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          aria-label={t('cases.filters.statusLabel')}
          className="px-3 py-2 text-sm border border-hairline-strong rounded-sm bg-surface-raised text-ink focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
        >
          <option value="">{t('cases.filters.allStatuses')}</option>
          {statusOptions.map((s) => (
            <option key={s} value={s}>
              {s.replace(/_/g, ' ')}
            </option>
          ))}
        </select>
      </div>

      <DataTable
        columns={columns}
        data={filteredCases}
        rowKey={(c) => c.id}
        onRowClick={(c) => navigate(`/cases/${c.id}`)}
        emptyMessage={t('cases.empty')}
        ariaLabel={t('cases.ariaLabel')}
        pageSize={PAGE_SIZE}
        nextCursor={nextCursor}
        hasPreviousPage={cursorStack.length > 0}
        onNextPage={(cursor) => {
          setCursorStack((prev) => [...prev, currentCursor]);
          setCurrentCursor(cursor);
          loadCases(cursor);
        }}
        onPreviousPage={() => {
          const stack = [...cursorStack];
          const previousCursor = stack.pop() ?? '';
          setCursorStack(stack);
          setCurrentCursor(previousCursor);
          loadCases(previousCursor || undefined);
        }}
      />
    </div>
  );
}
